import { SITE_URL, BRAND, LOGO_URL } from '@/components/seo/seo'
import type { BlogPost } from './posts'

const absolute = (path: string) => `${SITE_URL}${path}`

/** The MindWeb Organization, reused as author and publisher (matches marketing). */
const organization = {
  '@type': 'Organization',
  name: BRAND,
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
} as const

const langTag = (language: string) => (language === 'zh' ? 'zh-CN' : 'en')

interface Crumb {
  readonly name: string
  readonly path: string
}

function breadcrumbList(items: readonly Crumb[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absolute(item.path),
    })),
  }
}

/** BreadcrumbList for the /blog list page: Home → Blog. */
export function blogListBreadcrumb(blogLabel: string): Record<string, unknown> {
  return breadcrumbList([
    { name: 'Home', path: '/' },
    { name: blogLabel, path: '/blog' },
  ])
}

/** BreadcrumbList for an article: Home → Blog → <title>. */
export function blogPostBreadcrumb(
  blogLabel: string,
  postTitle: string,
  slug: string,
): Record<string, unknown> {
  return breadcrumbList([
    { name: 'Home', path: '/' },
    { name: blogLabel, path: '/blog' },
    { name: postTitle, path: `/blog/${slug}` },
  ])
}

/** BlogPosting schema for an article. Author + publisher = MindWeb Organization. */
export function blogPostingSchema(args: {
  readonly post: BlogPost
  readonly headline: string
  readonly description: string
  readonly language: string
}): Record<string, unknown> {
  const path = `/blog/${args.post.slug}`
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: args.headline,
    description: args.description,
    inLanguage: langTag(args.language),
    datePublished: args.post.date,
    dateModified: args.post.date,
    keywords: args.post.tags.join(', '),
    author: organization,
    publisher: organization,
    mainEntityOfPage: { '@type': 'WebPage', '@id': absolute(path) },
  }
}
